import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import useSilentIdTokenRefresh from "../msal/useSilentTokenRefresh";
import { useAppContext } from "../hooks/context/AppContext";
import useLocalStorage from "../hooks/useLocalStorage";
// import { useNavigate } from "react-router-dom";
// import { useQueryParams } from "../hooks/useQueryParams";
// import { useUserApi } from "../Services/api/userApi";
import { useHeader } from "../hooks/context/HeaderContext";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { errorToast, successToast } from "../Utils/toast";
import { usefileUploadApi } from "../Services/api/FileUploadApi";
import FileUpload from "../components/common/FileUpload";
import SelectDropdown from "../components/common/SelectDropdown";
import { ToastContainer } from "react-toastify";
import { useLoader } from "../components/common/LoaderContext";

interface AdminHomeProps {
  roleAvailable: boolean;
  userRole: string;
}

const schema = yup.object().shape({
  uploadPic: yup.mixed(),
});

const AdminHome: React.FC<AdminHomeProps> = ({ roleAvailable, userRole }) => {
  const { updateHeaderInfo } = useHeader();
  const { uploadImage, getMasterDataDetails } = usefileUploadApi();
  const { setLoading } = useLoader();

  const [idToken, setIdToken] = useState(null);
  const [, setAuthData] = useLocalStorage("auth_data", null);
  const { authData, updateAuthData, userData } = useAppContext();
  const [dropdownOptions, setDropdownOptions] = useState<
    {
      MasterDataName: any;
      MasterDataFileInfoID: any;
      key: string;
      value: string;
      MasterDataFleName: string;
      tooltip: string;
    }[]
  >([]);
  const [selectedDropdown, setSelectedDropdown] = useState<string>("");
  const [uploadedFileName, setUploadedFileName] = useState("");
  // const [dropdownError, setDropdownError] = useState<string>("");
  const [fileInputKey, setFileInputKey] = useState(0);
  const [formData, setFormData] = useState<{ uploadPic: string }>({
    uploadPic: "",
  });
  // const [loading, setLoading] = useState(false);
  const [validationMsg, setValidationMsg] = useState<React.ReactNode>(null);
  const [successValidationMsg, setSuccessValidationMsg] =
    useState<React.ReactNode>(null);
  const {
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const handleFileChange = async (file: File | undefined) => {
    setLoading(true);
    setValidationMsg("");
    if (!file) {
      setLoading(false);
      return;
    }

    // 1. Validate file extension
    const allowedExtensions = [".xlsx", ".xls"];
    const fileName = file.name;
    const fileExt = fileName.slice(fileName.lastIndexOf(".")).toLowerCase();
    setUploadedFileName(fileName);
    if (!allowedExtensions.includes(fileExt)) {
      setValidationMsg("Only .xlsx or .xls files are allowed.");
      setFileInputKey((prev) => prev + 1);
      setLoading(false);
      return;
    }

    // 2. Validate file name matches MasterDataFleName from selected dropdown
    const selectedOption = dropdownOptions.find(
      (opt) => String(opt.key) === String(selectedDropdown)
    );
    let masterDataFileName = "";
    let ADLSFolderPath = "";
    if (selectedOption && selectedOption.MasterDataFleName) {
      masterDataFileName = selectedOption.MasterDataFleName;
      setUploadedFileName(fileName);
    } else if (selectedOption) {
      // If dropdownOptions doesn't have MasterDataFleName, fetch from original API response
      const allOptions = await getMasterDataDetails();
      const fullOption = allOptions.find(
        (opt: any) =>
          String(opt.MasterDataFileInfoID) === String(selectedDropdown)
      );
      masterDataFileName = fullOption?.MasterDataFleName || "";
      ADLSFolderPath = fullOption?.ADLSFolderPath || "";
    }

    if (masterDataFileName && fileName !== masterDataFileName) {
      setValidationMsg(
        // File name does not match(should be ${masterDataFileName})`
        <>
          <div className="validationMsg">
            <strong style={{ color: "red" }}>Match Case is Failed :</strong>{" "}
            <br></br>
            <span className="selectedFiletext">
              You have selected{" "}
              <span className="masterFileSelect"> {selectedOption?.value}</span>{" "}
              as a Master Data, and
            </span>
            <br></br>
            You have Uploaded a{" "}
            <span className="selectedFile">{fileName} </span> File
            <br></br>
            <span className="matchFiletext">
              Please Browse / Upload{" "}
              <span className="matchFileSelect">{masterDataFileName} </span>
              &nbsp;file{" "}
            </span>
          </div>
        </>
      );

      setFileInputKey((prev) => prev + 1);
      setLoading(false);
      return;
    }
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
    if (file.size > MAX_FILE_SIZE) {
      errorToast("Image size must be less than 5 MB");
      setLoading(false);
      return;
    }
    try {
      // setLoading(true);
      const formdata = new FormData();
      formdata.append("file", file, file.name);
      let data = await uploadImage(formdata, { folderpath: ADLSFolderPath });
      if (data) {
        setSuccessValidationMsg(data);
        // setLoading(false);
        setFormData({
          ...formData,
          uploadPic: file.name,
        });
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      // setLoading(false);
      setValidationMsg("Failed to upload file. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // fetch data for the initial dropdown load
  useEffect(() => {
    updateHeaderInfo({
      landscapeName: "Upload - Master Data File",
    });
    const fetchOptions = async () => {
      setLoading(true);
      const options = await getMasterDataDetails();
      const mappedOptions = options.map(
        (opt: { MasterDataFileInfoID: any; MasterDataName: any }) => ({
          key: opt.MasterDataFileInfoID,
          value: opt.MasterDataName,
        })
      );
      setDropdownOptions(mappedOptions);

      if (mappedOptions.length > 0) {
        setSelectedDropdown(mappedOptions[0].key);
      }
      setLoading(false);
    };

    fetchOptions();
  }, []);

  useEffect(() => {
    setValidationMsg("");
    setSuccessValidationMsg("");
  }, [selectedDropdown]);
  return (
    <>
      <ToastContainer />
      <div>
        <Box sx={{ minHeight: "90vh" }}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={3}
          >
            <form>
              <Box
                alignItems="center"
                gap={4}
                mt={3}
                sx={{
                  backgroundColor: "#fff",
                  padding: "28px",
                  borderRadius: "16px",
                  boxShadow: "2px 2px 2px 0px #ccc",
                }}
              >
                <p className="masterFileInfo">
                  Select a Master Data and Browse to Excel (.xls or .xlsx) file{" "}
                  <i>(match the file with same name)</i>
                </p>
                <Box mr={1} mt={2}>
                  <div className="masterdata-select">
                    <Typography
                      sx={{
                        fontSize: "16px",
                        fontWeight: "bold",
                        textAlign: "center",
                      }}
                    >
                      &nbsp; Select Master Data
                    </Typography>
                    <Box className="admin-dropdown">
                      <SelectDropdown
                        key="masterdata"
                        label=""
                        value={selectedDropdown}
                        filterKey="masterdata"
                        options={dropdownOptions}
                        onChange={(val) => {
                          setSelectedDropdown(
                            Array.isArray(val) ? val[0] : val
                          );
                          setFormData({ ...formData, uploadPic: "" });
                        }}
                      />
                    </Box>
                  </div>
                </Box>
                {/* <br></br> */}
                <Box display="flex" flexDirection="column" alignItems="center">
                  <FileUpload
                    key={fileInputKey}
                    isDisabled={false}
                    onFileChange={handleFileChange}
                    name="uploadPic"
                    label="Browse File"
                    control={control}
                    errors={errors}
                  />
                  {/* Validation message above the browse button */}
                  {/* <br></br> */}
                  {validationMsg && <Typography>{validationMsg}</Typography>}
                  {successValidationMsg && (
                    <Typography
                      sx={{
                        backgroundColor: "#D5FFD2",
                        color: "#2a2a2a",
                        padding: "3px 10px;",
                        borderRadius: "10px",
                        border: "1px solid #71E267",
                        width: "94%",
                        mt: 1,
                        textAlign: "center",
                      }}
                    >
                      {successValidationMsg}
                    </Typography>
                  )}

                  {/* {uploadedFileName} */}
                  {/* You have Uploaded File: {uploadedFileName} */}
                </Box>
              </Box>
            </form>
          </Box>
        </Box>
      </div>
    </>
  );
};

export default AdminHome;
